import React, { useState, useEffect } from "react"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { InputAdornment } from "@mui/material"
import dayjs, { Dayjs } from "dayjs"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"

interface Props {
    fromDate?: Dayjs | null
    toDate?: Dayjs | null
    onChange: (range: { from: Date | null; to: Date | null }) => void
    setFromDate?: (val: Dayjs | null) => void
    setToDate?: (val: Dayjs | null) => void
    showFromDate?: boolean
    showToDate?: boolean
    fromLabel?: string
    toLabel?: string
    format?: string
}

const DateRangePicker: React.FC<Props> = ({
    onChange,
    fromDate = null,
    toDate = null,
    setFromDate,
    setToDate,
    showFromDate = true,
    showToDate = true,
    fromLabel = "From Date",
    toLabel = "To Date",
    format = "DD-MM-YYYY",
}) => {
    const [internalFrom, setInternalFrom] = useState<Dayjs | null>(fromDate)
    const [internalTo, setInternalTo] = useState<Dayjs | null>(toDate)
    const [openFrom, setOpenFrom] = useState(false)
    const [openTo, setOpenTo] = useState(false)

    // Keep internal state and external props in sync
    useEffect(() => {
        setInternalFrom(fromDate ?? null)
    }, [fromDate])

    useEffect(() => {
        setInternalTo(toDate ?? null)
    }, [toDate])

    // Whenever dates change, call onChange
    useEffect(() => {
        onChange({
            from: internalFrom ? internalFrom.toDate() : null,
            to: internalTo ? internalTo.toDate() : null,
        })
    }, [internalFrom, internalTo])

    const handleFromChange = (newValue: Dayjs | null) => {
        setInternalFrom(newValue)
        if (setFromDate) setFromDate(newValue)
        if (internalTo && newValue && newValue.isAfter(internalTo)) {
            setInternalTo(null)
            if (setToDate) setToDate(null)
        }
    }

    const handleToChange = (newValue: Dayjs | null) => {
        setInternalTo(newValue)
        if (setToDate) setToDate(newValue)
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="flex flex-nowrap gap-4 flex-col screen400:flex-row">
                {showFromDate && (
                    <DatePicker
                        label={fromLabel}
                        format={format}
                        value={internalFrom}
                        onChange={handleFromChange}
                        maxDate={internalTo ?? dayjs()}
                        open={openFrom}
                        onOpen={() => setOpenFrom(true)}
                        onClose={() => setOpenFrom(false)}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                inputProps: {
                                    readOnly: true,
                                    onClick: () => setOpenFrom(!openFrom),
                                },
                                InputProps: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <ArrowDropDownIcon
                                                sx={{ cursor: "pointer" }}
                                                onClick={() =>
                                                    setOpenFrom(!openFrom)
                                                }
                                            />
                                        </InputAdornment>
                                    ),
                                },
                                sx: {
                                    height: 44,
                                    "& .MuiInputLabel-root": {
                                        fontSize: "0.775rem",
                                    },
                                    "& .MuiInputBase-root": {
                                        height: 44,
                                        fontSize: "12px",
                                    },
                                },
                            },
                        }}
                    />
                )}
                {showToDate && (
                    <DatePicker
                        label={toLabel}
                        value={internalTo}
                        format={format}
                        onChange={handleToChange}
                        minDate={internalFrom ?? undefined}
                        maxDate={dayjs()}
                        open={openTo}
                        onOpen={() => setOpenTo(true)}
                        onClose={() => setOpenTo(false)}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                inputProps: {
                                    readOnly: true,
                                    onClick: () => setOpenTo(!openTo),
                                },
                                InputProps: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <ArrowDropDownIcon
                                                sx={{ cursor: "pointer" }}
                                                onClick={() =>
                                                    setOpenTo(!openTo)
                                                }
                                            />
                                        </InputAdornment>
                                    ),
                                },
                                sx: {
                                    height: 44,
                                    "& .MuiInputLabel-root": {
                                        fontSize: "0.775rem",
                                    },
                                    "& .MuiInputBase-root": {
                                        height: 44,
                                        fontSize: "12px",
                                    },
                                },
                            },
                        }}
                    />
                )}
            </div>
        </LocalizationProvider>
    )
}

export default DateRangePicker
